/**
 * Keywords module.
 * All things to do with the keywords used to create a document.
 * Methods and types
 * I'm making the decision to use colons as part of the keywords.
 * I think it'll make them easier to distinguish in code mirror and hopefully typescript will keep tabs
 * on the obvious opportunity for confusion.
 * @module Keywords
 */

import type { AllModifiersType, ModifierAutoComplete, ModifiersSubSet } from "./modifiers";

export type TextAnimationModifiersType = "~";

export const colorsInBoostOrder = [
  "eighth",
  "seventh",
  "sixth",
  "fifth",
  "fourth",
  "third",
  "second",
  "first",
] as const;

// Modifier groups to reduce repetition
const COMMON_TEXT_MODIFIERS = [
  "color:",
  "font:",
  "size:",
  "weight:",
  "line-height:",
  "shadow:",
  "decoration:",
  "opacity:",
] as const;

const COMMON_POSITION_MODIFIERS = [
  "across:",
  "align:",
  "anchor:",
  "down:",
] as const;

const COMMON_ANIMATION_MODIFIERS = [
  "animate:",
  "animateStart:",
  "delay:",
] as const;

const COMMON_LINK_MODIFIERS = [
  "backlink:",
  "toPage:",
  "url:",
  "mailto:",
] as const;

const COMMON_BOX_MODIFIERS = [
  "border-color:",
  "border-thickness:",
  "corner-radius:",
  "padding-bottom:",
  "padding-left:",
  "padding-right:",
  "padding-top:",
  "padding:",
] as const;

// Composed modifier sets for different element types
const TEXT_ELEMENT_MODIFIERS: AllModifiersType[] = [
  ...COMMON_TEXT_MODIFIERS,
  ...COMMON_POSITION_MODIFIERS,
  ...COMMON_ANIMATION_MODIFIERS,
  "background:",
  "columns:",
  "hideOn:",
];

const TEXT_WITH_LINKS_MODIFIERS: AllModifiersType[] = [
  ...TEXT_ELEMENT_MODIFIERS,
  ...COMMON_LINK_MODIFIERS,
];

const BULLET_MODIFIERS: AllModifiersType[] = [
  ...TEXT_ELEMENT_MODIFIERS,
];

const MEDIA_MODIFIERS: AllModifiersType[] = [
  ...COMMON_POSITION_MODIFIERS,
  ...COMMON_ANIMATION_MODIFIERS,
  ...COMMON_BOX_MODIFIERS,
  "hideOn:",
  "height:",
  "width:",
  "opacity:",
];

const BOX_ELEMENT_MODIFIERS: AllModifiersType[] = [
  ...TEXT_ELEMENT_MODIFIERS,
  ...COMMON_BOX_MODIFIERS,
  "blur:",
];

export const keywords = [
  "##:",
  "#:",
  "**:",
  "*:",
  "audio:",
  "autoplay:",
  "autospace:",
  "background:",
  "button:",
  "blank:",
  "box:",
  "chart:",
  "columnTitle:",
  "filter:",
  "form:",
  "image:",
  "line:",
  "moveBy:",
  "map:",
  "paragraph:",
  "pause:",
  "read:",
  "storyName:",
  "subtitle:",
  "supertitle:",
  "title:",
  "video:",
] as const;



export const notTextKeywords = [
  "audio:",
  "background:",
  "blank:",
  "chart:",
  "columnTitle:",
  "filter:",
  "image:",
  "map:",
  "moveBy:",
  "storyName:",
  "pause:",
  "video:",
] as const;

export const notDisplayedKeywords = [
  "autoplay:",
  "filter:",
  "moveBy:",
  "pause:",
  "read:",
  "storyName:"
]

export const cannotBeFirstElements = [...notDisplayedKeywords, "background:", "blank:"]



export type KeywordsType = typeof keywords[number];



export type TopLevel = "topLevel";

export const keywordCategories = ["text", "media", "divider", "bullet", "uncategorized", "loadable"] as const;

export type KeywordsObjectType = {
  [K in KeywordsType]: {
    allowNdr: boolean; // whether or not to allow the content to be processed for neuro-divergent readers
    autoComplete?: ModifierAutoComplete;
    type: typeof keywordCategories[number];
    help: string;
    expandedHelp?: string; // NEW FIELD
    hide?: boolean;        // NEW FIELD
    keyword: KeywordsType;
    modifiers: AllModifiersType[];
  };
};

export const keywordsObject: KeywordsObjectType = {
  "*:": {
    allowNdr: true,
    keyword: "*:",
    help: "Make a bullet point.",
    expandedHelp: "Just type `*:` followed by your text to make a bullet point. Like this: `*: Add your text here`",
    hide: false,
    type: "bullet",
    modifiers: BULLET_MODIFIERS,
  },
  "**:": {
    allowNdr: true,
    keyword: "**:",
    type: "bullet",
    help: "Indent a bullet point.",
    expandedHelp: "To indent a bullet point, just use `**:`. It's perfect for making sub-lists. Like this: `**: This is an indented bullet`",
    hide: false,
    modifiers: BULLET_MODIFIERS,
  },
  "#:": {
    allowNdr: true,
    keyword: "#:",
    type: "bullet",
    help: "Make a numbered list.",
    expandedHelp: "Use `#: ` to start a numbered list. QuickPoint handles the numbering for you. For example: `#: First item`",
    hide: false,
    modifiers: BULLET_MODIFIERS,
  },
  "##:": {
    allowNdr: true,
    keyword: "##:",
    type: "bullet",
    help: "Indent a numbered list item.",
    expandedHelp: "For sub-items in a numbered list, just use `##:`. Like this: `##: Sub-item 1.1`",
    hide: false,
    modifiers: BULLET_MODIFIERS,
  },
  "audio:": {
    allowNdr: false,
    keyword: "audio:",
    type: "media",
    help: "Add an audio file.",
    expandedHelp: "Got an MP3 or OGG file? Just type `audio:` and then pick the audio file you want to add from your list of uploaded files.",
    hide: false,
    modifiers: [],
    autoComplete: ["audio"],
  },
  "autoplay:": {
    allowNdr: false,
    keyword: "autoplay:",
    type: "uncategorized",
    help: "Have your story play automatically.",
    expandedHelp: "Want your story to move to the next slide on its own? Just type `autoplay:` and a number of seconds. For example, `autoplay: 5` will move things along after 5 seconds. You can even make it loop with the `loop:` modifier.",
    hide: true,
    modifiers: ["loop:"],
    autoComplete: ["loop"],
  },
  "autospace:": {
    allowNdr: false,
    keyword: "autospace:",
    type: "uncategorized",
    help: "Toggles automatic vertical spacing between elements.",
    expandedHelp: "Use `autospace: off` to manually control the vertical space between elements using the `blank:` keyword. By default, autospacing is on.",
    hide: false,
    modifiers: [],
    autoComplete: ["toggle"]
  },
  "background:": {
    allowNdr: false,
    keyword: "background:",
    type: "media",
    help: "Change the background to a color or an image.",
    expandedHelp: "You can set a background for your slide with a color or an image. Just type `background:` and then a color name (like `blue`) or pick an image you've uploaded.",
    hide: false,
    modifiers: ["opacity:", "animate:", "animateStart:"],
    autoComplete: ["colors", "images"],
  },
  "blank:": {
    allowNdr: false,
    keyword: "blank:",
    type: "media",
    help: "Add some empty space.",
    expandedHelp: "Use `blank:` to add a standard amount of space. Want more? Just add a number. `blank:2` gives you double the space, and `blank:3` gives you triple. It's an easy way to get your spacing just right.",
    hide: false,
    modifiers: ["height:"],
  },
  "box:": {
    allowNdr: true,
    keyword: "box:",
    type: "divider",
    help: "Highlight text in a stylish box.",
    expandedHelp: "Perfect for pulling out a quote or making a key point stand out. You can even give the box its own background color to make it pop.",
    hide: false,
    modifiers: BOX_ELEMENT_MODIFIERS,
  },
  "button:": {
    allowNdr: true,
    keyword: "button:",
    type: "text",
    help: "Make a clickable button.",
    expandedHelp: "Use this to create a button that links to another page in your story (with `toPage:`) or to a different website (with `url:`).",
    hide: false,
    modifiers: TEXT_WITH_LINKS_MODIFIERS,
  },
  "chart:": {
    allowNdr: false,
    keyword: "chart:",
    type: "media",
    help: "Create a chart from your data.",
    expandedHelp: "Turn your data into a chart. Just upload a CSV, Excel or JSON file, type `chart:`, and then pick your file.",
    hide: false,
    modifiers: ["chart-type:", "animate:", "animateStart:"],
    autoComplete: ["charts"],
  },
  "columnTitle:": {
    allowNdr: true,
    keyword: "columnTitle:",
    type: "text",
    help: "Defines a title for a grid column, enabling responsive behavior on mobile.",
    expandedHelp: "Use `columnTitle:` in the first row of a grid to label each column. Grids with column titles will automatically collapse into a stacked layout on mobile screens for better readability.",
    hide: false,
    modifiers: TEXT_ELEMENT_MODIFIERS,
  },
  "filter:": {
    allowNdr: false,
    keyword: "filter:",
    type: "uncategorized",
    help: "Apply a complete visual style to your page.",
    expandedHelp: "A filter takes care of all the visual elements for you, providing a pre-chosen color palette and style that applies to everything on the page.",
    hide: false,
    modifiers: [],
    autoComplete: ["filters"],
  },
  "form:": {
    allowNdr: true,
    keyword: "form:",
    type: "uncategorized",
    help: "Ask your audience a question.",
    expandedHelp: "Use this to create a form and gather information from your readers.",
    hide: true,
    modifiers: BOX_ELEMENT_MODIFIERS,
  },
  "image:": {
    allowNdr: false,
    keyword: "image:",
    type: "loadable",
    help: "Add an image from your files.",
    expandedHelp: "Just type `image:` and pick the image you want to add. You can also add a `caption:` or make the image a link with `url:` or `toPage:`.",
    hide: false,
    autoComplete: ["images"],
    modifiers: [...MEDIA_MODIFIERS, ...COMMON_LINK_MODIFIERS, "background:", "blur:", "caption:"],
  },
  "line:": {
    allowNdr: false,
    keyword: "line:",
    type: "divider",
    help: "Add a decorative line to break up your page.",
    expandedHelp: "Add a line to separate your content. Make it thicker by adding a number (e.g., `line:2`). Or, add some text to create a more decorative divider (e.g., `line: Chapter 2`).",
    hide: false,
    modifiers: ["animate:", "animateStart:", "color:", "font:", "line-height:", "opacity:", "size:", "weight:"],
  },
  "map:": {
    allowNdr: false,
    keyword: "map:",
    type: "media",
    help: "Add a Google Map.",
    expandedHelp: "Write a place name right after `map:` (for example `map: Niger Delta, Nigeria`) and QuickPoint maps it. You can also paste a link copied from Google Maps' Share button.",
    hide: true,
    modifiers: MEDIA_MODIFIERS,
  },
  "moveBy:": {
    allowNdr: false,
    keyword: "moveBy:",
    type: "uncategorized",
    help: "Choose how to move through your story.",
    expandedHelp: "Do you want your audience to tap or scroll? Use `moveBy: tap` to have them click through, or `moveBy: scroll` to let them scroll.",
    hide: false,
    modifiers: [],
    autoComplete: ["moveBy"],
  },
  "paragraph:": {
    allowNdr: true,
    keyword: "paragraph:",
    type: "text",
    help: "Add a paragraph of text.",
    expandedHelp: "This is the best way to add regular text to your page. You can change how it looks with modifiers like `color:`, `size:`, and `font:`.",
    hide: false,
    modifiers: TEXT_WITH_LINKS_MODIFIERS,
  },
  "read:": {
    allowNdr: true,
    keyword: "read:",
    type: "text",
    help: "Add text for 'Read mode'.",
    expandedHelp: "This text won't show up on your page, but it will be available in 'Read mode'. This is a great place for speaker notes or extra information for your audience.",
    hide: false,
    modifiers: TEXT_ELEMENT_MODIFIERS,
  },
  // "shadertoy:": {
  //   allowNdr: false,
  //   keyword: "shadertoy:",
  //   type: "media",
  //   help: "Adds a 3D background from shadertoy.com",
  //   expandedHelp: "",
  //   hide: false,
  //   modifiers: [],
  // },
  "storyName:": {
    allowNdr: false,
    keyword: "storyName:",
    type: "uncategorized",
    help: "Give your story a name.",
    expandedHelp: "The name you choose will show up in the browser tab. It's also what people will see when you share your story. For example: `storyName: My Story's Title`",
    hide: false,
    modifiers: [],
  },
  "subtitle:": {
    allowNdr: true,
    keyword: "subtitle:",
    type: "text",
    help: "Add a subtitle.",
    expandedHelp: "Perfect for adding a bit more information right under your main title.",
    hide: false,
    modifiers: TEXT_WITH_LINKS_MODIFIERS,
  },
  "supertitle:": {
    allowNdr: true,
    keyword: "supertitle:",
    type: "text",
    help: "Add a big, bold title.",
    expandedHelp: "This creates text that's even bigger than a normal title. It's great for making a big statement or introducing a new section.",
    hide: false,
    modifiers: TEXT_WITH_LINKS_MODIFIERS,
  },
  "pause:": {
    allowNdr: false,
    keyword: "pause:",
    type: "uncategorized",
    help: "Pause the story.",
    expandedHelp: "Everything after a `pause:` will be hidden until your reader clicks or taps. It's a great way to reveal your points one by one.",
    hide: false,
    modifiers: [],
  },
  "title:": {
    allowNdr: true,
    keyword: "title:",
    type: "text",
    help: "Add a title.",
    expandedHelp: "Use this to add a main title to your page.",
    hide: false,
    modifiers: TEXT_WITH_LINKS_MODIFIERS,
  },
  "video:": {
    allowNdr: false,
    keyword: "video:",
    type: "media",
    help: "Add a video from YouTube or Vimeo.",
    expandedHelp: "To add a video, go to its page on YouTube or Vimeo and get the shareable link. Then, just paste that link right after `video:`.",
    hide: false,
    modifiers: [...COMMON_POSITION_MODIFIERS, ...COMMON_ANIMATION_MODIFIERS, "hideOn:", "caption:", "url:", "mute:"],
  },
};

/**
 * Get the relevant keywords based on the parent keyword
 * @param {string} keyword - The parent keyword. If no parent is supplied it returns the top level keywords.
 * @returns {array} An array of keywords
 */
export const getKeywordsArr = (
  keyword: typeof keywords[number] | TopLevel,
): typeof keywords | ModifiersSubSet => {
  if (keyword === "topLevel") {
    return keywords;
  }
  return keywordsObject[keyword].modifiers;
};

export const getKeywordHelpText = (index: AllModifiersType | KeywordsType) => {
  // @ts-expect-error: too hard to type
  if (keywords.includes(index)) {
    // @ts-expect-error: too hard to type
    return keywordsObject[index].help;
  }
  // @ts-expect-error: too hard to type
  if (modifiers.includes(index)) {
    // @ts-expect-error: too hard to type
    return modifierObject[index].help;
  }
};

export function isKeyword(arg: unknown) {
  // @ts-expect-error: I think typescript types Array.includes too tightly to be useful
  if (keywords.includes(arg)) {
    return true;
  }
  return false;
}

export function keyWordsWithAutoComplete() {
  const res = [];
  for (const key in keywordsObject) {
    const k = key as typeof keywords[number];
    if (keywordsObject[k].autoComplete && !keywordsObject[k].hide) {
      res.push(k);
    }
  }
  return res;
}
